import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../services/ApiService";

/* ── Logo ──────────────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="cd-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" /><stop offset="50%" stopColor="#00E5CC" /><stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#cd-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#cd-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#cd-ng)">J</text>
  </svg>
);

const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12l5 5L19 7" /></svg>,
};

interface CaseStudy {
  _id: string; slug: string; title: string; content: string;
  coverImage?: string; client?: string; industry?: string;
  results?: string[]; challenge?: string; solution?: string;
}

export default function CaseStudyPublicDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getMediaUrl = (url?: string | null): string => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = process.env.REACT_APP_MEDIA_BASE || "";
    if (url.startsWith("/uploads/")) return `${base}${url}`;
    return `${base}/uploads/case-studies/${url}`;
  };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = (await api.get(`/case-studies/${slug}`)) as any;
        const data = res.caseStudy || res.data?.caseStudy || res.data || null;
        if (!data || data.success === false) { setError("Case study not found"); return; }
        setCaseStudy(data);
      } catch (err) {
        setError("Failed to load case study");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  // Loading / Error / Not found states
  if (loading) return (
    <div className="csd-root"><style dangerouslySetInnerHTML={{ __html: CSD_STYLES }} /><div className="csd-msg">Loading...</div></div>
  );
  if (error) return (
    <div className="csd-root"><style dangerouslySetInnerHTML={{ __html: CSD_STYLES }} /><div className="csd-msg error">{error}</div></div>
  );
  if (!caseStudy) return (
    <div className="csd-root"><style dangerouslySetInnerHTML={{ __html: CSD_STYLES }} />
      <div className="csd-msg">Case study not found. <a href="#" onClick={e => { e.preventDefault(); navigate('/case-studies'); }}>← Back to all case studies</a></div>
    </div>
  );

  return (
    <div className="csd-root">
      <style dangerouslySetInnerHTML={{ __html: CSD_STYLES }} />

      {/* NAV */}
      <nav className="csd-nav">
        <a href="#" className="csd-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} /><span className="csd-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
        </a>
        <button className="csd-back" onClick={() => navigate('/case-studies')}>
          <span className="csd-ico-16">{Ico.arrowLeft}</span> All Case Studies
        </button>
      </nav>

      {/* ARTICLE */}
      <article className="csd-article">

        {/* COVER */}
        {caseStudy.coverImage && (
          <img src={getMediaUrl(caseStudy.coverImage)} className="csd-cover" alt={caseStudy.title} />
        )}

        <h1>{caseStudy.title}</h1>

        {/* META */}
        {(caseStudy.client || caseStudy.industry) && (
          <div className="csd-meta">
            {caseStudy.client && <span className="csd-client">Client: {caseStudy.client}</span>}
            {caseStudy.industry && <span className="csd-industry">Industry: {caseStudy.industry}</span>}
          </div>
        )}

        {/* CHALLENGE */}
        {caseStudy.challenge && (
          <div className="csd-section-card">
            <h2>The Challenge</h2>
            <p>{caseStudy.challenge}</p>
          </div>
        )}

        {/* SOLUTION */}
        {caseStudy.solution && (
          <div className="csd-section-card">
            <h2>Our Solution</h2>
            <p>{caseStudy.solution}</p>
          </div>
        )}

        {/* RESULTS */}
        {caseStudy.results && caseStudy.results.length > 0 && (
          <div className="csd-section-card results">
            <h2>Results</h2>
            <div className="csd-results-list">
              {caseStudy.results.map((result, idx) => (
                <div key={idx} className="csd-result-item">
                  <span className="csd-check">{Ico.check}</span>
                  <span>{result}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTENT */}
        <div className="csd-content" dangerouslySetInnerHTML={{ __html: caseStudy.content }} />
      </article>

      {/* BACK CTA */}
      <div className="csd-bottom-cta">
        <button className="csd-btn-back" onClick={() => navigate('/case-studies')}>
          <span className="csd-ico-16">{Ico.arrowLeft}</span> Back to All Case Studies
        </button>
      </div>
    </div>
  );
}

const CSD_STYLES = `
.csd-root *,.csd-root *::before,.csd-root *::after{margin:0;padding:0;box-sizing:border-box}
.csd-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;padding-bottom:100px;
}
.csd-root a{color:var(--cyan);text-decoration:none}
.csd-root a:hover{text-decoration:underline}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.csd-ico-16 svg{width:16px;height:16px}

.csd-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.csd-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none!important;cursor:pointer}
.csd-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.csd-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.csd-back:hover{border-color:var(--cyan);color:var(--cyan)}

.csd-msg{text-align:center;padding:140px 24px 60px;color:rgba(224,240,240,0.4);font-size:16px}
.csd-msg.error{color:#f87171}

.csd-article{max-width:800px;margin:0 auto;padding:120px 24px 0}

.csd-cover{width:100%;max-height:440px;object-fit:cover;border-radius:20px;border:1px solid rgba(0,229,204,0.06);margin-bottom:32px;box-shadow:0 20px 60px rgba(0,0,0,0.3)}

.csd-article h1{font-size:clamp(28px,5vw,44px);font-weight:800;line-height:1.15;letter-spacing:-1.5px;margin-bottom:16px}

.csd-meta{display:flex;align-items:center;gap:20px;flex-wrap:wrap;margin-bottom:32px;font-size:13px}
.csd-client{font-weight:600;color:var(--cyan)}
.csd-industry{color:rgba(224,240,240,0.4);font-weight:600;letter-spacing:.5px;text-transform:uppercase;font-size:11px}

/* SECTION CARDS */
.csd-section-card{padding:28px;border-radius:18px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);margin-bottom:24px}
.csd-section-card h2{font-size:20px;font-weight:700;margin-bottom:12px;color:#e0f0f0}
.csd-section-card p{font-size:15px;line-height:1.7;color:rgba(224,240,240,0.55)}
.csd-section-card.results{border-color:rgba(0,229,204,0.12);background:rgba(0,229,204,0.03)}

.csd-results-list{display:flex;flex-direction:column;gap:12px}
.csd-result-item{display:flex;align-items:flex-start;gap:12px;font-size:14px;color:rgba(224,240,240,0.6);line-height:1.5}
.csd-check{width:20px;height:20px;flex-shrink:0;color:var(--teal);margin-top:1px;display:flex}
.csd-check svg{width:20px;height:20px}

/* CONTENT */
.csd-content{margin-top:32px;font-size:16px;line-height:1.8;color:rgba(224,240,240,0.65)}
.csd-content h1,.csd-content h2,.csd-content h3,.csd-content h4{color:#e0f0f0;font-weight:700;margin-top:32px;margin-bottom:12px}
.csd-content h2{font-size:24px}.csd-content h3{font-size:20px}
.csd-content p{margin-bottom:16px}
.csd-content a{color:var(--cyan)}
.csd-content ul,.csd-content ol{padding-left:24px;margin-bottom:16px}
.csd-content li{margin-bottom:8px}
.csd-content blockquote{border-left:3px solid var(--cyan);padding:16px 20px;margin:24px 0;background:rgba(0,229,204,0.04);border-radius:0 12px 12px 0;font-style:italic;color:rgba(224,240,240,0.5)}
.csd-content img{max-width:100%;border-radius:12px;margin:24px 0}

/* BOTTOM CTA */
.csd-bottom-cta{max-width:800px;margin:48px auto 0;padding:0 24px}
.csd-btn-back{display:inline-flex;align-items:center;gap:8px;padding:14px 28px;border-radius:12px;border:1.5px solid rgba(0,229,204,0.25);background:rgba(0,229,204,0.04);color:var(--cyan);font-family:'Sora',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:all .3s}
.csd-btn-back:hover{border-color:var(--cyan);background:rgba(0,229,204,0.08)}

@media(max-width:900px){.csd-nav{padding:14px 20px}.csd-article{padding:100px 20px 0}}
`;
